import { Stack, Typography, Button, useTheme, Box, CircularProgress, Tooltip } from "@mui/material";
import { WhiteBox } from "@foodpilot/foods";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import { ScoreValue } from "./ScoreValue";
import { ScoreComparison } from "src/components/Score/ScoreComparison.tsx";
import { ScoreSourceChip } from "src/components/Score/ScoreSourceChip";
import SpecificityRateDisplay from "src/components/Subscore/SpecificityRate/SpecificityRateDisplay.tsx";
import { ScoreWithVariation } from "src/services/ScoreService";
import { MainScore, ScoreSourceMeta } from "src/api/client-api/olapScores/olapScoresSchema";
import { TFunction } from "i18next";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { ReferenceEntityData } from "../ScoreScatterPlot";

export type ScoreBoxProps = {
    title: string;
    score?: MainScore;
    prevScore?: MainScore;
    scoreWithVariation: ScoreWithVariation;
    referenceScore: number | null;
    prevCampaignName?: string;
    isRefetching?: boolean;
    onScoreDistributionClick?: (scoreId: number) => void;
    atLeastOnePostLocked?: boolean;
    referenceEntityData?: ReferenceEntityData;
};

export const ScoreBox = (props: ScoreBoxProps) => {
    const { score, scoreWithVariation, onScoreDistributionClick } = props;

    const theme = useTheme();
    const { isFeatureActivated } = useApplicationParameters();
    const { t } = useTranslation();

    const scoreValue = score?.score.value;
    const canDrilldown = isFeatureActivated("FS-ingredient-score-details-distribution");

    return (
        <WhiteBox sx={{ flex: "0 0 calc(33.33% - 14px)", minWidth: "fit-content" }}>
            <ScoreBoxCore {...props} />
            {canDrilldown && scoreValue !== null && onScoreDistributionClick && (
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        gap: theme.spacing(3.5),
                        padding: theme.spacing(3.5, 4),
                        borderTop: `1px solid ${theme.custom.grey[500]}`,
                    }}
                >
                    <Button
                        variant="ternary"
                        onClick={() => onScoreDistributionClick(scoreWithVariation.scoreId)}
                        endIcon={<ArrowForward />}
                        sx={{
                            flex: 1,
                            justifyContent: "space-between",
                            minHeight: "40px",
                            border: `1px solid ${theme.custom.grey[500]}`,
                            borderRadius: "44px",
                            background: "linear-gradient(180deg, #FFF 0%, #FAFAFA 100%)",
                            color: theme.custom.grey[2800],
                            fontWeight: 700,
                            padding: "0px 20px",
                        }}
                    >
                        {t("voir_plus_de_detail")}
                    </Button>
                </Stack>
            )}
        </WhiteBox>
    );
};

export const ScoreBoxCore = (props: ScoreBoxProps) => {
    const {
        title,
        score,
        prevScore,
        scoreWithVariation,
        referenceScore,
        prevCampaignName = "",
        isRefetching = false,
        atLeastOnePostLocked = false,
        referenceEntityData: {
            aggregateContributors = [],
            aggregateSourceMeta = null,
            isInheritedFromReference = false,
        } = {},
    } = props;

    const { t } = useTranslation();
    const { selectedVariations } = useEntityScores();

    const scoreValue = OlapScoresService.normalizeNumericalScore(score?.score.value);
    const prevScoreValue = OlapScoresService.normalizeNumericalScore(prevScore?.score.value);

    const displayedVariationId = selectedVariations.query?.[scoreWithVariation.scoreId] ?? null;
    const isReferenceScore = referenceScore !== null && !isRefetching && !displayedVariationId;

    const specificityRateScore = OlapScoresService.getSubscoreWithType("specificity-rate", score);
    const specificityRate = OlapScoresService.normalizeNumericalScore(specificityRateScore?.score.value);

    const scoreValueElement =
        !isRefetching && score ?
            <ScoreValue
                variant="h1"
                score={score}
                precision={scoreWithVariation.precision}
                sx={{
                    flexDirection: "row",
                    gap: "10px",
                    alignItems: "flex-end",
                }}
            />
        :   <CircularProgress />;

    // The chip should appear whenever at least one contributor is reference-backed
    // (supplier_reference / ingredient_reference / sure). Pure SSQ → empty list
    // here (the chip itself filters ssq/none entries away) → no badge.
    const shouldRenderSourceChip = aggregateContributors.some(
        (c) => c.source === "supplier_reference" || c.source === "ingredient_reference" || c.source === "sure",
    );

    return (
        <Stack sx={(theme) => ({ padding: theme.spacing(3.5, 4), gap: theme.spacing(2) })}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap="12px">
                <Typography variant="h3">{title}</Typography>
                {shouldRenderSourceChip && <ScoreSourceChip contributors={aggregateContributors} />}
            </Stack>

            <Stack>
                {isInheritedFromReference ?
                    <Tooltip title={getInheritedTooltip(t, aggregateSourceMeta)} arrow>
                        <Box
                            sx={{
                                cursor: "help",
                                width: "fit-content",
                                textDecoration: "underline dotted",
                                textUnderlineOffset: "6px",
                            }}
                        >
                            {scoreValueElement}
                        </Box>
                    </Tooltip>
                :   scoreValueElement}
            </Stack>

            {!isRefetching && prevScoreValue !== null && (
                <ScoreComparison
                    improvement={scoreWithVariation.improvement}
                    referenceScoreValue={prevScoreValue}
                    scoreValueToCompare={scoreValue}
                    unit={scoreWithVariation.unit}
                    reportName={prevCampaignName}
                />
            )}

            {isReferenceScore && (
                <ScoreComparison
                    improvement={scoreWithVariation.improvement}
                    referenceScoreValue={referenceScore}
                    scoreValueToCompare={scoreValue}
                    unit={scoreWithVariation.unit}
                    reportName={t("score_reference.the_reference")}
                    isInheritedFromReference={isInheritedFromReference}
                    sourceMeta={aggregateSourceMeta}
                />
            )}

            {atLeastOnePostLocked && specificityRate && <SpecificityRateDisplay specificityRate={specificityRate} />}
        </Stack>
    );
};

/**
 * Source-aware tooltip text used when the ingredient score was inherited from
 * a reference. Falls back to the legacy generic copy when the OLAP endpoint
 * doesn't surface meta (older response payloads or non-Ingredient entities).
 *
 * Wording is intentionally neutral — these are normal cascade outcomes, not
 * "legacy" paths (cf. product feedback Apr-26). All keys live under
 * `score_source.*` and are consumed identically by `ScoreSourceChip`.
 */
const getInheritedTooltip = (t: TFunction, meta: ScoreSourceMeta | null): string => {
    if (!meta) return t("score_reference.inherited_from_reference_tooltip");
    switch (meta.source) {
        case "supplier_reference":
            return meta.owner?.name ?
                    t("score_source.supplier_reference.tooltip_with_owner", {
                        defaultValue: "Score from {{name}}'s supplier reference.",
                        name: meta.owner.name,
                    })
                :   t("score_source.supplier_reference.tooltip", {
                        defaultValue: "Score from a supplier reference.",
                    });
        case "ingredient_reference":
            return t("score_source.ingredient_reference.tooltip", {
                defaultValue: "Score from a default ingredient (platform reference).",
            });
        case "sure":
            return t("score_source.sure.tooltip", {
                defaultValue: "Score from the Sure reference.",
            });
        case "ssq":
        case "none":
            return t("score_reference.inherited_from_reference_tooltip");
    }
};
